import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { getHelpDetailStack } from 'features/help/detail/actions';
import { getHelpStack } from 'features/help/actions';
import { setTopNavColorDark } from 'features/navigation/header/actions';
import styled from 'styled-components';
import { palette } from 'theme';
import dayjs from 'dayjs';
import loadable from '@loadable/component';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';

import Container from 'components/Container';
import ContentTypography from 'components/ContentTypography';
import MetaTags from 'components/MetaTags';
import SwiftypeTags from 'components/SwiftypeTags';
import PaddedDivider from 'components/PaddedDivider';

import ChevronRightIcon from 'mdi-material-ui/ChevronRight';

const StyledAvatar = loadable(() => import('components/StyledAvatar'));
const VideoPlayer = loadable(() => import('components/VideoPlayer'));
const HelpContact = loadable(() => import('../elements/HelpContact'));
const Table = loadable(() => import('components/Table'));

const SlimMixedRelatedContent = loadable(
  () => import('components/RelatedContent'),
  {
    resolveComponent: components => components.SlimMixedRelatedContent
  }
);

const HelpContainer = styled(Container)`
  margin-top: 40px;
  margin-bottom: 40px;
`;

const CategoryLink = styled(Typography)`
  text-decoration: none;
  margin-right: 10px;
  &:hover {
    color: ${palette.primary.main};
  }
`;

const RelatedPaper = styled(Paper)`
  padding: 20px;
  background: ${palette.grey[50]};
  margin-bottom: 20px;
`;

const RelatedLink = styled(Typography)`
  text-decoration: none;
  &:hover {
    color: ${palette.primary.main};
  }
  margin: 8px 0;
  padding-top: 8px;
`;

const CategorySpacer = styled(Typography)`
  margin-right: 10px;
  color: ${palette.grey[400]};
`;

const SmallChevronRightIcon = styled(ChevronRightIcon)`
  width: 16px;
  height: 16px;
  margin: 0 10px -3px 0;
  color: ${palette.grey[400]};
`;

const ProductTypography = styled(Typography)`
  margin-right: 10px;
`;

const ArticleAuthorAvatar = styled(StyledAvatar)`
  margin-right: 10px;
  display: inline-block;
`;

const HelpVideoPlayer = styled(VideoPlayer)`
  margin-bottom: 20px;
`;

class HelpDetail extends React.PureComponent {
  componentWillMount() {
    this.props.setTopNavColorDark();
  }

  componentDidMount() {
    this.props.getHelpDetailStack(this.props.match.url);
    this.props.getHelpStack();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.match.url !== this.props.match.url) {
      this.props.getHelpDetailStack(this.props.match.url);
    }
  }

  render() {
    const { stack, loading, contact } = this.props;

    return !loading ? (
      <Fragment>
        <MetaTags stack={stack} />
        <SwiftypeTags
          stack={stack}
          title={stack.title}
          publishedAt={stack.updated_at}
          contentType="Help Article"
          image="https://images.contentstack.io/v3/assets/blte41c17d7f8dda379/blt6eaeee4d25cb2c6f/5b108d0edc1dd5cd0f3cafad/OG_Image@2x.png"
          body={stack.body}
          excerpt={stack.body}
          tags={stack.categories.map(cat => cat.title)}
        />
        <HelpContainer>
          <Grid container spacing={40}>
            <Grid item xs={12} md={12}>
              <CategoryLink
                variant="overline"
                component={Link}
                to={`/help`}
                inline
                gutterBottom
              >
                Help Center
              </CategoryLink>
              <SmallChevronRightIcon />
              {stack.categories.map((category, idx) => (
                <Fragment key={idx}>
                  <CategoryLink
                    variant="overline"
                    component={Link}
                    to={`/help/category/${category.slug}`}
                    inline
                    gutterBottom
                  >
                    {category.title}
                  </CategoryLink>
                  {idx !== stack.categories.length - 1 && (
                    <CategorySpacer variant="overline" inline gutterBottom>
                      &bull;
                    </CategorySpacer>
                  )}
                </Fragment>
              ))}
            </Grid>
          </Grid>
          <Grid container spacing={40}>
            <Grid item xs={12} md={8}>
              <Typography variant="h3" component="h1">
                {stack.title}
              </Typography>
              <ContentTypography
                variant="body2"
                dangerouslySetInnerHTML={{ __html: stack.body }}
                gutterBottom
              />

              {(stack.videos || []).map((video, idx) => (
                <PrerenderSafeLazyLoad>
                  <HelpVideoPlayer {...video} />
                </PrerenderSafeLazyLoad>
              ))}
              {(stack.tables || []).map((tableGroup, idx) => (
                <PrerenderSafeLazyLoad>
                  {tableGroup.content && (
                    <ContentTypography
                      variant="body2"
                      dangerouslySetInnerHTML={{ __html: tableGroup.content }}
                      gutterBottom
                    />
                  )}
                  <Table {...tableGroup?.table?.[0]?.table} />
                  <PaddedDivider hide />
                </PrerenderSafeLazyLoad>
              ))}
              {stack?.categories_tags?.product?.length > 0 && (
                <>
                  <ProductTypography variant="caption" inline gutterBottom>
                    <strong>Products:</strong>
                  </ProductTypography>
                  {(stack?.categories_tags?.product || []).map(
                    (product, idx) => (
                      <ProductTypography
                        variant="caption"
                        component={Link}
                        to={product.page_url}
                        key={idx}
                        inline
                      >
                        {product.name}
                      </ProductTypography>
                    )
                  )}
                  <PaddedDivider spacing={1.25} />
                </>
              )}
              <Grid container spacing={40}>
                <PrerenderSafeLazyLoad>
                  <Grid item xs={12}>
                    <Typography variant="overline" align="right" inline>
                      {stack.created_at && (
                        <>
                          {'Posted: '}
                          <span>
                            {dayjs(stack.created_at, 'YYYY-MM-DD').format(
                              'MMMM DD, YYYY'
                            )}
                          </span>
                        </>
                      )}
                      {stack.updated_at && (
                        <>
                          {`, `}
                          {' Last Updated: '}
                          <span>
                            {dayjs(stack.updated_at, 'YYYY-MM-DD').format(
                              'MMMM DD, YYYY'
                            )}
                          </span>
                        </>
                      )}
                    </Typography>
                  </Grid>
                  {stack.author && stack.author.length > 0 && (
                    <Grid item xs={12} container alignItems="center">
                      {stack.author[0].bio_image && (
                        <ArticleAuthorAvatar
                          size="50px"
                          src={`${stack.author[0].bio_image.url}?format=pjpg&width=100&auto=webp`}
                        />
                      )}
                      <Typography variant="subtitle2" inline>
                        {stack.author[0].name}
                      </Typography>
                    </Grid>
                  )}
                </PrerenderSafeLazyLoad>
                <Grid item xs={12}>
                  {stack.other_related_content &&
                    stack.other_related_content.length > 0 && (
                      <PrerenderSafeLazyLoad>
                        <RelatedPaper elevation={0}>
                          <Typography variant="h6" gutterBottom>
                            Related Content
                          </Typography>
                          <PaddedDivider spacing={0} />
                          <SlimMixedRelatedContent
                            contentItems={stack.other_related_content}
                          />
                        </RelatedPaper>
                      </PrerenderSafeLazyLoad>
                    )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              {stack.related_help_articles &&
                stack.related_help_articles.length > 0 && (
                  <PrerenderSafeLazyLoad>
                    <RelatedPaper elevation={0}>
                      <Typography variant="h6" gutterBottom>
                        Related Help Articles
                      </Typography>
                      <PaddedDivider spacing={0} />
                      {stack.related_help_articles.map((article, idx) => (
                        <RelatedLink
                          variant="button"
                          component={Link}
                          to={article.url}
                          key={idx}
                        >
                          {article.title}
                        </RelatedLink>
                      ))}
                    </RelatedPaper>
                  </PrerenderSafeLazyLoad>
                )}
              {contact && (
                <PrerenderSafeLazyLoad>
                  <HelpContact
                    text={contact.text}
                    button={contact.button}
                    align="left"
                    hideDivider
                  />
                </PrerenderSafeLazyLoad>
              )}
            </Grid>
          </Grid>
        </HelpContainer>
      </Fragment>
    ) : null;
  }
}

HelpDetail.propTypes = {
  stack: PropTypes.object
};

export default connect(
  state => ({
    stack: state.helpDetail.stack,
    loading: state.helpDetail.loading,
    contact: state.helpLanding.stack.contact
  }),
  {
    getHelpDetailStack,
    getHelpStack,
    setTopNavColorDark
  }
)(HelpDetail);
